import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Loader2, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";

import { GET_BILLING_STATUS } from "@/graphql/queries/students";
import { START_CHECKOUT } from "@/graphql/mutations/students";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { OrderDialog, formatPrice, type CreditCost, type Offer } from "@/components/billing/order-dialog";
import {
  CancelSubscriptionButton,
  ManageBillingButton,
  subscriptionSentence,
} from "@/components/billing/subscription-controls";

/**
 * What this account has paid for, and how to change it.
 *
 * Nothing here grants anything. Every button hands the browser to Stripe's own
 * page; the subscription and the credits arrive from the webhook when Stripe
 * says the money did. That is why the return from a payment says "kelias
 * sekundes" rather than showing a new balance — the balance is the webhook's to
 * write, and it may land a moment after the redirect.
 */

type Status = {
  subscribed: boolean;
  plan: string | null;
  subscribedUntil: string | null;
  aiCredits: number;
  monthlyAiCredits: number;
  metered: boolean;
  paymentsEnabled: boolean;
  stripeSubscription: boolean;
  cancelAtPeriodEnd: boolean;
  cancelable: boolean;
  canManageBilling: boolean;
  holderId: string;
  offers: Offer[];
  creditCosts: CreditCost[];
};

const PLAN_LABEL: Record<string, string> = { monthly: "Mėnesinė", yearly: "Metinė" };

export default function BillingPage() {
  const [params, setParams] = useSearchParams();
  const { data, loading, refetch } = useQuery(GET_BILLING_STATUS, {
    fetchPolicy: "cache-and-network",
  });
  const [startCheckout, { loading: starting }] = useMutation(START_CHECKOUT);
  const [pending, setPending] = useState<string | null>(null);
  // The plan whose order dialog is open. Nothing is bought from a plan button
  // directly: the pre-contract information comes first, then the order button.
  const [ordering, setOrdering] = useState<Offer | null>(null);

  const status: Status | null = (data as any)?.billingStatus ?? null;

  // Coming back from Stripe. The webhook usually lands first, but not always —
  // so say what is happening and look again shortly, rather than showing a stale
  // balance and letting them think the payment failed.
  useEffect(() => {
    if (params.get("paid")) {
      toast.success(
        "Ačiū! Mokėjimas gautas — kreditai atsiras per kelias sekundes, o užsakymo patvirtinimą atsiųsime el. paštu."
      );
      const timer = setTimeout(() => refetch(), 3000);
      params.delete("paid");
      params.delete("session_id");
      setParams(params, { replace: true });
      return () => clearTimeout(timer);
    }
    // Back from the portal's cancellation flow. The webhook carries the change;
    // look again shortly so the page says „atšaukta" without a reload.
    if (params.get("cancelled_subscription")) {
      toast.success("Prenumerata atšaukta. Ji galios iki apmokėto laikotarpio pabaigos.");
      const timer = setTimeout(() => refetch(), 3000);
      params.delete("cancelled_subscription");
      setParams(params, { replace: true });
      return () => clearTimeout(timer);
    }
    if (params.get("cancelled")) {
      toast("Mokėjimas atšauktas — nieko nenuskaityta.");
      params.delete("cancelled");
      setParams(params, { replace: true });
    }
  }, [params, refetch, setParams]);

  const order = async (offer: Offer) => {
    const plan = offer.plan;
    setPending(plan);
    try {
      // The version of the consent text the dialog showed and the buyer ticked;
      // the server refuses a plan that needs one without it.
      const result = await startCheckout({
        variables: { plan, consentVersion: offer.consentVersion ?? undefined },
      });
      const payload = (result.data as any)?.startCheckout;
      if (payload?.url) {
        window.location.assign(payload.url);
        return;
      }
      toast.error(payload?.errors?.[0] ?? "Nepavyko pradėti mokėjimo");
    } catch {
      toast.error("Nepavyko pradėti mokėjimo");
    } finally {
      setPending(null);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No account behind this session. Every sign-in belongs to an account, so this
  // is only reached when the account was deleted while its cookie lived:
  // `billingStatus` answers null. Saying something beats an empty page.
  if (!status) {
    return (
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Prenumerata ir kreditai</h1>
        </div>
        <Card>
          <CardContent className="space-y-2 py-6 text-sm text-muted-foreground">
            <p>Šios paskyros nebėra arba prisijungimas nebegalioja.</p>
            <p>
              Atsijunkite ir prisijunkite iš naujo su savo el. paštu arba Google paskyra —
              tada čia matysite prenumeratą ir kreditus.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const offerFor = (plan: string) => status.offers.find((o) => o.plan === plan) ?? null;
  const open = (plan: string) => setOrdering(offerFor(plan));
  const priced = (plan: string, label: string) => {
    const offer = offerFor(plan);
    const price = offer ? formatPrice(offer) : null;
    return price ? `${label} · ${price}` : label;
  };

  const used = Math.max(0, status.monthlyAiCredits - status.aiCredits);
  const percentLeft = status.monthlyAiCredits > 0
    ? Math.min(100, Math.round((status.aiCredits / status.monthlyAiCredits) * 100))
    : 0;

  return (
    <div className="max-w-3xl space-y-6">
      <OrderDialog
        offer={ordering}
        creditCosts={status.creditCosts}
        pending={starting}
        onOpenChange={(isOpen) => !isOpen && !starting && setOrdering(null)}
        onConfirm={order}
      />
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Prenumerata ir kreditai</h1>
        <p className="text-muted-foreground">
          AI funkcijos — pokalbiai apie užduotis, ataskaitos, užduočių pritaikymas ir avatarai —
          naudoja kreditus. Kiekvieną mėnesį jie atsinaujina.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-brand-accent" />
              Kreditai
            </CardTitle>
            <CardDescription>
              {status.metered ? (
                <>
                  Liko <strong className="text-foreground tabular-nums">{status.aiCredits}</strong>{" "}
                  iš {status.monthlyAiCredits}. Šį mėnesį panaudota {used}.
                </>
              ) : (
                "Ši paskyra neribojama — AI kreditai iš jos neskaičiuojami."
              )}
            </CardDescription>
          </div>
          {status.subscribed ? (
            <Badge className="bg-primary/10 text-primary hover:bg-primary/10">
              {status.plan ? PLAN_LABEL[status.plan] ?? status.plan : "Aktyvi"}
              {status.subscribedUntil && ` iki ${status.subscribedUntil}`}
            </Badge>
          ) : (
            <Badge variant="secondary">Be prenumeratos</Badge>
          )}
        </CardHeader>
        {status.metered && (
          <CardContent>
            <Progress value={percentLeft} className="h-2" />
            {status.aiCredits === 0 && (
              <p className="mt-3 text-sm text-muted-foreground">
                Kreditai baigėsi. Užduotys, teorija ir sąvokų kortelės veikia kaip įprastai — AI
                pokalbis ir ataskaitos palauks kito mėnesio arba papildymo.
              </p>
            )}
          </CardContent>
        )}
      </Card>

      {!status.paymentsEnabled ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Mokėjimai kol kas neįjungti. Prenumeratą gali suteikti administratorius.
          </CardContent>
        </Card>
      ) : (
        <>
          {status.stripeSubscription ? (
            // A running Stripe subscription is changed and cancelled where it
            // lives — a second checkout would open a second, double-charging
            // one, which `startCheckout` refuses anyway.
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Jūsų prenumerata
                </CardTitle>
                <CardDescription>{subscriptionSentence(status)}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                {status.cancelable && <CancelSubscriptionButton />}
                <ManageBillingButton variant="outline" />
              </CardContent>
            </Card>
          ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                {status.subscribed ? "Pakeisti planą" : "Pradėti prenumeratą"}
              </CardTitle>
              <CardDescription>
                Kiekvieną mėnesį — {status.monthlyAiCredits} kreditų. Atšaukti galima bet kada.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button onClick={() => open("monthly")} disabled={starting}>
                {pending === "monthly" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {priced("monthly", "Mėnesinė")}
              </Button>
              <Button onClick={() => open("yearly")} disabled={starting} variant="outline">
                {pending === "yearly" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {priced("yearly", "Metinė")}
              </Button>
            </CardContent>
          </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Papildyti kreditus
              </CardTitle>
              <CardDescription>
                Vienkartinis pirkinys, jei kreditai baigėsi anksčiau laiko. Nepanaudoti papildymo
                kreditai lieka ir kitą mėnesį.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button onClick={() => open("credits_500")} disabled={starting} variant="outline">
                {pending === "credits_500" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {priced("credits_500", "+500 kreditų")}
              </Button>
              <Button onClick={() => open("credits_2000")} disabled={starting} variant="outline">
                {pending === "credits_2000" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {priced("credits_2000", "+2000 kreditų")}
              </Button>
            </CardContent>
          </Card>

          {/* Cards and invoices for an account that bought before, even with
              no subscription running now. */}
          {status.canManageBilling && !status.stripeSubscription && (
            <p className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
              <ManageBillingButton /> — atidaroma „Stripe“ puslapyje.
            </p>
          )}
        </>
      )}
    </div>
  );
}
